import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { academyAdminAPI } from '../../lib/academyApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from 'recharts';
import {
  DollarSign, Users, TrendingUp, Calendar, Award,
  BookOpen, Loader2, ArrowUpRight,
} from 'lucide-react';

const COLORS = ['#F9B438', '#1D1D1B', '#3b82f6', '#22c55e', '#f97316', '#a855f7', '#ef4444', '#14b8a6', '#6366f1', '#ec4899'];
const STATUS_LABELS = { pending: 'En attente', registered: 'Inscrit', approved: 'Approuve', paid: 'Paye', invoice: 'Facture', awaiting_contact: 'A contacter', completed: 'Complete', cancelled: 'Annule', stripe: 'Stripe', contacted: 'Contacte' };

const StatCard = ({ icon: Icon, label, value, sub, color, link }) => (
  <Link to={link || '#'}>
    <Card className="hover:shadow-md transition-shadow group cursor-pointer" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-5 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}15` }}>
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-2xl font-bold text-[#1D1D1B]">{value}</p>
          <p className="text-xs text-neutral-500">{label}</p>
          {sub && <p className="text-[10px] text-neutral-400">{sub}</p>}
        </div>
        <ArrowUpRight className="w-4 h-4 text-neutral-300 group-hover:text-[#F9B438] transition-colors" />
      </CardContent>
    </Card>
  </Link>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium text-[#1D1D1B] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="text-xs">{p.name}: {typeof p.value === 'number' && p.name.includes('$') ? `${p.value.toFixed(0)} $` : p.value}</p>
      ))}
    </div>
  );
};

const AcademyAnalytics = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/connexion'); return; }
    academyAdminAPI.getAnalytics()
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user, navigate]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" /></div>;
  if (!data) return <p className="text-center py-20 text-neutral-400">Erreur de chargement</p>;

  const { summary: s, revenue_by_training, monthly_trends, session_fill_rates, status_distribution, payment_distribution } = data;

  const statusPie = Object.entries(status_distribution).map(([k, v]) => ({ name: STATUS_LABELS[k] || k, value: v }));
  const paymentPie = Object.entries(payment_distribution).map(([k, v]) => ({ name: STATUS_LABELS[k] || k, value: v }));

  return (
    <div className="space-y-6" data-testid="academy-analytics">
      {/* KPI Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={DollarSign} label="Revenus totaux" value={`${s.total_revenue.toLocaleString('fr-CA')} $`} sub="Tous paiements confondus" color="#22c55e" link="/academy/paiements" />
        <StatCard icon={Users} label="Inscriptions" value={s.active_registrations} sub={`${s.total_registrations} total dont actives`} color="#3b82f6" link="/academy/inscriptions" />
        <StatCard icon={Calendar} label="Sessions ouvertes" value={s.open_sessions} sub={`${s.total_sessions} sessions au total`} color="#F9B438" link="/academy/sessions" />
        <StatCard icon={Award} label="Certificats emis" value={s.total_certificates} color="#a855f7" link="/academy/certificats" />
      </div>

      {/* Revenue by training */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><DollarSign className="w-4 h-4 text-[#F9B438]" />Revenus par formation</CardTitle></CardHeader>
        <CardContent>
          {revenue_by_training.length === 0 ? (
            <p className="text-center py-8 text-neutral-400 text-sm">Aucune donnee de revenus</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(250, revenue_by_training.length * 40)}>
              <BarChart data={revenue_by_training} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `${v} $`} />
                <YAxis dataKey="title" type="category" width={200} tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="revenue" name="Revenus $" fill="#F9B438" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Monthly trends */}
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><TrendingUp className="w-4 h-4 text-[#F9B438]" />Tendances mensuelles</CardTitle></CardHeader>
          <CardContent>
            {monthly_trends.length === 0 ? (
              <p className="text-center py-8 text-neutral-400 text-sm">Pas assez de donnees</p>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={monthly_trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => `${v}$`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line yAxisId="left" type="monotone" dataKey="inscriptions" name="Inscriptions" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                  <Line yAxisId="right" type="monotone" dataKey="revenus" name="Revenus $" stroke="#22c55e" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Status & Payment distribution */}
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><BookOpen className="w-4 h-4 text-[#F9B438]" />Repartition</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium text-neutral-500 text-center mb-2">Par statut</p>
                {statusPie.length === 0 ? (
                  <p className="text-center text-neutral-300 text-xs py-4">Aucune donnee</p>
                ) : (
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie data={statusPie} cx="50%" cy="50%" innerRadius={35} outerRadius={60} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                        {statusPie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
              <div>
                <p className="text-xs font-medium text-neutral-500 text-center mb-2">Par mode de paiement</p>
                {paymentPie.length === 0 ? (
                  <p className="text-center text-neutral-300 text-xs py-4">Aucune donnee</p>
                ) : (
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie data={paymentPie} cx="50%" cy="50%" innerRadius={35} outerRadius={60} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                        {paymentPie.map((_, i) => <Cell key={i} fill={COLORS[(i + 3) % COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Session fill rates */}
      {session_fill_rates.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Calendar className="w-4 h-4 text-[#F9B438]" />Taux de remplissage des sessions</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {session_fill_rates.map((s, i) => (
                <div key={i} className="flex items-center gap-4" data-testid={`fill-rate-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-[#1D1D1B] truncate">{s.title}</span>
                      <span className="text-xs text-neutral-400 ml-2 flex-shrink-0">{s.date} - {s.registered}/{s.max}</span>
                    </div>
                    <div className="h-2.5 bg-neutral-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.min(s.fill_rate, 100)}%`,
                          background: s.fill_rate >= 90 ? '#ef4444' : s.fill_rate >= 60 ? '#F9B438' : '#22c55e',
                        }}
                      />
                    </div>
                  </div>
                  <Badge className={`text-xs ${s.fill_rate >= 90 ? 'bg-red-100 text-red-700' : s.fill_rate >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                    {s.fill_rate}%
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revenue table */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><DollarSign className="w-4 h-4 text-[#F9B438]" />Detail par formation</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-neutral-500">
                  <th className="py-2 pr-4">Formation</th>
                  <th className="py-2 pr-4 text-right">Inscriptions</th>
                  <th className="py-2 pr-4 text-right">Payes</th>
                  <th className="py-2 text-right">Revenus</th>
                </tr>
              </thead>
              <tbody>
                {revenue_by_training.map((t, i) => (
                  <tr key={i} className="border-b border-neutral-50 hover:bg-neutral-50">
                    <td className="py-2.5 pr-4 font-medium text-[#1D1D1B]">{t.title}</td>
                    <td className="py-2.5 pr-4 text-right">{t.registrations}</td>
                    <td className="py-2.5 pr-4 text-right">{t.paid}</td>
                    <td className="py-2.5 text-right font-medium text-[#22c55e]">{t.revenue.toLocaleString('fr-CA')} $</td>
                  </tr>
                ))}
                <tr className="font-bold">
                  <td className="py-3 pr-4">Total</td>
                  <td className="py-3 pr-4 text-right">{revenue_by_training.reduce((a, b) => a + b.registrations, 0)}</td>
                  <td className="py-3 pr-4 text-right">{revenue_by_training.reduce((a, b) => a + b.paid, 0)}</td>
                  <td className="py-3 text-right text-[#22c55e]">{revenue_by_training.reduce((a, b) => a + b.revenue, 0).toLocaleString('fr-CA')} $</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AcademyAnalytics;
